/**
 * Iter 89.3 / Sprint 2A.2 — ActivityTimelineStrip
 * ─────────────────────────────────────────────────────────────────
 * Renders the 288-slot (5-min resolution) presence timeline returned
 * by /api/workforce/desktop-activity/me/timeline and the symmetric
 * admin/manager /timeline endpoint.
 *
 * Pure presentational. Stateless. Accepts the raw `timeline` array
 * straight from the backend response — no shape transformation
 * happens here.
 *
 * Bucket colours:
 *   active  → emerald
 *   idle    → amber
 *   locked  → slate-700 (dark)
 *   offline → slate-200 (light, "no observation")
 *
 * Privacy: no screen / app / URL / key data ever passes through
 * this component. The only thing rendered is dominant-share buckets
 * + per-window seconds totals (active/idle/locked).
 */
import { useMemo } from "react";

const BUCKET_CLASS = {
    active:  "bg-emerald-500",
    idle:    "bg-amber-400",
    locked:  "bg-slate-700",
    offline: "bg-slate-200",
};

const BUCKET_LABEL = {
    active:  "Active",
    idle:    "Idle",
    locked:  "Locked",
    offline: "No data",
};

function fmtSecs(s) {
    const total = Number(s) || 0;
    if (total === 0) return "0";
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const r = total % 60;
    if (h > 0) return `${h}h ${m}m ${r}s`;
    if (m > 0) return `${m}m ${r}s`;
    return `${r}s`;
}

function slotLabel(slot, slotMinutes) {
    const minutesFromMidnight = slot * slotMinutes;
    const h = Math.floor(minutesFromMidnight / 60);
    const m = minutesFromMidnight % 60;
    const hh = String(h).padStart(2, "0");
    const mm = String(m).padStart(2, "0");
    return `${hh}:${mm}`;
}

export function ActivityTimelineStrip({
    timeline = [],
    slotMinutes = 5,
    slotsPerDay = 288,
    testIdPrefix = "timeline",
}) {
    // Defensive padding/truncation so the strip always renders exactly
    // slotsPerDay cells even if the server returned a different shape.
    const cells = useMemo(() => {
        const padded = Array.from({ length: slotsPerDay }, (_, i) => (
            timeline[i] || {
                slot: i,
                bucket: "offline",
                active_seconds: 0,
                idle_seconds: 0,
                locked_seconds: 0,
            }
        ));
        return padded;
    }, [timeline, slotsPerDay]);

    // Hour tick positions — 24 hours from 00 to 23.
    const hourTicks = useMemo(
        () => Array.from({ length: 24 }, (_, i) => i),
        []
    );

    return (
        <div
            className="w-full select-none"
            data-testid={`${testIdPrefix}-strip`}
        >
            {/* Strip — 288 cells in a single horizontal grid */}
            <div
                className="grid w-full h-9 rounded overflow-hidden border border-slate-200 bg-slate-100"
                style={{ gridTemplateColumns: `repeat(${slotsPerDay}, 1fr)` }}
                role="img"
                aria-label="Activity timeline strip"
            >
                {cells.map((cell, idx) => {
                    const bucketClass = BUCKET_CLASS[cell.bucket] || BUCKET_CLASS.offline;
                    const bucketLabel = BUCKET_LABEL[cell.bucket] || BUCKET_LABEL.offline;
                    const startLabel = slotLabel(idx, slotMinutes);
                    const endLabel = slotLabel(idx + 1, slotMinutes);
                    const tooltip =
                        `${startLabel}–${endLabel} · ${bucketLabel}` +
                        (cell.bucket !== "offline"
                            ? ` · active ${fmtSecs(cell.active_seconds)} · idle ${fmtSecs(cell.idle_seconds)} · locked ${fmtSecs(cell.locked_seconds)}`
                            : "");
                    return (
                        <div
                            key={idx}
                            className={`${bucketClass} h-full`}
                            title={tooltip}
                            data-testid={`${testIdPrefix}-slot-${idx}`}
                            data-bucket={cell.bucket}
                        />
                    );
                })}
            </div>

            {/* Hour-tick axis */}
            <div
                className="grid w-full mt-1 text-[9px] text-slate-400 font-mono tabular-nums"
                style={{ gridTemplateColumns: `repeat(24, 1fr)` }}
                aria-hidden="true"
            >
                {hourTicks.map((h) => (
                    <div key={h} className="text-center leading-none">
                        {String(h).padStart(2, "0")}
                    </div>
                ))}
            </div>

            {/* Legend */}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-3 text-[11px] text-slate-600" data-testid={`${testIdPrefix}-legend`}>
                <LegendDot cls={BUCKET_CLASS.active}  label="Active" />
                <LegendDot cls={BUCKET_CLASS.idle}    label="Idle" />
                <LegendDot cls={BUCKET_CLASS.locked}  label="Locked" />
                <LegendDot cls={BUCKET_CLASS.offline} label="No data" />
                <span className="text-slate-400 ml-auto">{slotMinutes}-min resolution · 24h local time</span>
            </div>
        </div>
    );
}

function LegendDot({ cls, label }) {
    return (
        <span className="inline-flex items-center gap-1.5">
            <span className={`inline-block w-2.5 h-2.5 rounded-sm ${cls}`} />
            {label}
        </span>
    );
}

export default ActivityTimelineStrip;
